import { Dropdown, Menu } from "antd";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import Tooltip from "components/Tooltip/Tooltip";
import React, { PropsWithChildren, ReactNode } from "react";
import "./DropdownButton.scss";

export const ActionDropdownButton = ({
    parentAction,
    label,
    iconByName,
    icon,
    isDisabled,
    children,
    dropdownIcon,
    tooltipOverlay,
    image,
    overlayClassName,
    isLoading,
    size,
}: any) => {
    const getIcon = ({ icon, iconByName }) =>
        icon ? <Icon icon={icon} byName={iconByName} /> : null;

    const overlay = <Menu>{children}</Menu>;
    const buttonsRender = tooltipOverlay?.length
        ? {
              buttonsRender: ([leftButton, rightButton]: ReactNode[]) =>
                  [
                      <Tooltip overlay={tooltipOverlay} placement="top" key="leftButton">
                          {leftButton}
                      </Tooltip>,
                      React.cloneElement(rightButton as React.ReactElement<any, string>),
                  ] as ReactNode[],
          }
        : {};

    return (
        <Dropdown.Button
            className={`actionDropdownButton${isDisabled ? " button--disabled" : ""}${
                isLoading ? " button--isLoading" : ""
            }${size === "small" ? " actionDropdownButton--small" : ""}`}
            onClick={parentAction}
            overlay={overlay}
            icon={dropdownIcon}
            overlayClassName={overlayClassName}
            {...buttonsRender}
        >
            {image ?? getIcon({ icon, iconByName })}
            {label}
        </Dropdown.Button>
    );
};

interface ActionDropdownButtonWithoutParentProps {
    isDisabled?: boolean;
    dropdownIcon?: ReactNode;
    dropdownLabel?: string;
    trigger?: ("contextMenu" | "click" | "hover")[];
    className?: string;
    tooltipOverlay?: string;
    image?: ReactNode;
    /**
     * Optional controlled open-state (antd v4 `visible`/`onVisibleChange`).
     * When both are provided the dropdown is controlled, so a consumer can
     * close it programmatically (e.g. after an overlay item opens a modal —
     * the custom <Button> children are not antd Menu.Items, so antd does not
     * auto-close the overlay on click). When omitted the dropdown stays
     * uncontrolled (unchanged behaviour for every other consumer).
     */
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ActionDropdownButtonWithoutParent = ({
    isDisabled,
    children,
    dropdownIcon,
    dropdownLabel,
    trigger,
    className,
    tooltipOverlay,
    image,
    visible,
    onVisibleChange,
}: PropsWithChildren<ActionDropdownButtonWithoutParentProps>) => {
    const overlay = <Menu>{children}</Menu>;
    const triggerButton = (
        <Button
            image={image}
            icon={dropdownIcon}
            label={dropdownLabel}
            disabled={isDisabled}
            variation="dark"
        />
    );
    return (
        <Dropdown
            trigger={trigger ?? ["click", "hover"]}
            className={`actionDropdownButton${isDisabled ? " button--disabled" : ""} ${className}`}
            overlay={overlay}
            disabled={isDisabled}
            visible={visible}
            onVisibleChange={onVisibleChange}
        >
            {tooltipOverlay ? (
                <div>
                    <Tooltip overlay={tooltipOverlay} align={{ offset: [0, 15] }}>
                        {triggerButton}
                    </Tooltip>
                </div>
            ) : (
                triggerButton
            )}
        </Dropdown>
    );
};
